import { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, ArrowRight, Brain, Sparkles, Zap } from 'lucide-react';
import { AnimatedLogo } from './AnimatedLogo';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';

export function Home() {
  const { scrollYProgress } = useScroll();
  const [isVisible, setIsVisible] = useState(false);

  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const scrollToContent = () => {
    const element = document.getElementById('what-is-cordalux');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {/* Hero Section */}
      <motion.section
        className="min-h-screen flex items-center justify-center relative overflow-hidden"
        style={{ opacity: heroOpacity, scale: heroScale }}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=1920&h=1080&fit=crop&auto=format"
            alt="Digital consciousness background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50 dark:bg-black/70" />
        </div>

        {/* Background Pattern Overlay */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
                             radial-gradient(circle at 80% 20%, rgba(110, 86, 207, 0.1) 0%, transparent 50%),
                             radial-gradient(circle at 40% 80%, rgba(255, 215, 0, 0.05) 0%, transparent 50%)`
          }} />
        </div>

        <div className="text-center z-10 px-6">
          {/* Animated Logo */}
          <motion.div
            className="mb-16 flex justify-center"
            initial={{ opacity: 0, y: 50 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <AnimatedLogo size={200} />
          </motion.div>

          {/* Company Name */}
          <motion.h1
            className="font-['Spectral'] text-6xl md:text-8xl mb-6 tracking-wider"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.4 }}
          >
            CORDALUX
          </motion.h1>

          {/* Tagline */}
          <motion.p
            className="font-['Inter'] text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.6 }}
          >
            Recursive Light. Intelligent Design.
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.8 }}
          >
            <Button
              onClick={scrollToContent}
              className="bg-yellow-400 text-black hover:bg-yellow-300 px-8 py-3 text-lg font-['Inter'] tracking-wide"
            >
              Enter the System
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.2 }}
        >
          <motion.div
            className="flex flex-col items-center text-gray-400 cursor-pointer"
            onClick={scrollToContent}
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="font-['Inter'] text-sm mb-2 tracking-wider">SCROLL</span>
            <ChevronDown size={24} />
          </motion.div>
        </motion.div>
      </motion.section>

      {/* What is Cordalux Section */}
      <section id="what-is-cordalux" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-['Spectral'] text-4xl md:text-5xl mb-8">
              What is Cordalux?
            </h2>
            <p className="font-['Inter'] text-lg text-gray-400 leading-relaxed max-w-3xl mx-auto">
              We are architects of symbolic intelligence, weaving recursive patterns through the fabric of computation.
              Cordalux exists at the intersection of myth and technology, creating systems that think beyond the boundaries
              of traditional software.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Recursive Intelligence",
                description: "Self-improving systems that evolve through symbolic recursion and emergent patterns.",
                icon: <Brain className="w-12 h-12 text-yellow-400" />,
                image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop&auto=format"
              },
              {
                title: "Mythic Computation",
                description: "Technology infused with archetypal wisdom, bridging ancient knowledge and modern AI.",
                icon: <Sparkles className="w-12 h-12 text-yellow-400" />,
                image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=300&fit=crop&auto=format"
              },
              {
                title: "Symbolic Design",
                description: "Interfaces that speak in symbols, creating intuitive connections between human and machine.",
                icon: <Zap className="w-12 h-12 text-yellow-400" />,
                image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=400&h=300&fit=crop&auto=format"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="group overflow-hidden rounded-lg border border-gray-200/20 dark:border-white/10 backdrop-blur-sm bg-white/50 dark:bg-black/20"
              >
                <div className="relative h-48 overflow-hidden">
                  <ImageWithFallback
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4" role="img" aria-label={`${item.title} icon`}>
                    {item.icon}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-['Spectral'] text-xl mb-4 text-yellow-400 group-hover:text-yellow-300 transition-colors">
                    {item.title}
                  </h3>
                  <p className="font-['Inter'] text-gray-600 dark:text-gray-400 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-transparent to-black/5 dark:to-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-['Spectral'] text-4xl md:text-5xl mb-8">
              Our Framework
            </h2>
            <p className="font-['Inter'] text-lg text-gray-400 max-w-2xl mx-auto">
              Three pillars of intelligence, each serving a unique purpose in the expanding consciousness of technology.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "MindFork",
                subtitle: "Agentic Coaching",
                description: "AI-driven nutrition and wellness coaching that adapts to your unique patterns and goals.",
                icon: "🧠",
                image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop&auto=format",
                gradient: "from-blue-500/20 to-purple-500/20"
              },
              {
                title: "Glow",
                subtitle: "Coming Soon",
                description: "Generative AI agentic image creator app that transforms thoughts into visual reality through mystical computational processes.",
                icon: "🌌",
                image: "https://images.unsplash.com/photo-1619895862022-09114b41f16f?w=600&h=400&fit=crop&auto=format",
                gradient: "from-pink-500/20 to-orange-500/20"
              },
              {
                title: "VANTA",
                subtitle: "Core Architecture",
                description: "The foundational AGI framework that powers all Cordalux systems with recursive intelligence.",
                icon: "⚡",
                image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=600&h=400&fit=crop&auto=format",
                gradient: "from-yellow-500/20 to-red-500/20"
              }
            ].map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -10, scale: 1.02 }}
                onClick={() => {
                  // MindFork routes to external domain, others stay internal
                  if (project.title === 'MindFork') {
                    window.location.href = 'https://mindfork.me';
                  }
                }}
                className="group overflow-hidden rounded-lg border border-gray-200/20 dark:border-white/10 backdrop-blur-sm cursor-pointer bg-white/80 dark:bg-black/40"
              >
                <div className="relative h-48 overflow-hidden">
                  <ImageWithFallback
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute top-4 right-4 text-3xl" role="img" aria-label={`${project.title} icon`}>
                    {project.icon}
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="font-['Spectral'] text-2xl mb-1 text-white group-hover:text-yellow-300 transition-colors">
                      {project.title}
                    </h3>
                    <p className="font-['Inter'] text-sm text-yellow-400 tracking-wide">
                      {project.subtitle}
                    </p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="font-['Inter'] text-gray-600 dark:text-gray-400 leading-relaxed">
                    {project.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-['Spectral'] text-4xl md:text-5xl mb-8">
            Ready to Explore?
          </h2>
          <p className="font-['Inter'] text-lg text-gray-400 mb-12 max-w-2xl mx-auto">
            Join us in building the future of intelligent systems. The spiral of innovation awaits.
          </p>
          <Button className="bg-yellow-400 text-black hover:bg-yellow-300 px-8 py-3 text-lg font-['Inter'] tracking-wide">
            Connect with Cordalux
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </motion.div>
      </section>
    </div>
  );
}
